/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: 扫描覆盖层组件 - 提供激光扫描特效动画
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-07-24 11:49:49
 */
import React from 'react'
import './index.scss'

/**
 * 扫描覆盖层组件属性接口
 */
export interface ScanningOverlayProps {
  /** 是否显示扫描特效 */
  visible?: boolean
  /** 扫描区域宽度 */
  width?: number
  /** 扫描区域高度 */
  height?: number
  /** 激光颜色 */
  laserColor?: string
  /** 扫描速度（秒） */
  duration?: number
  /** 自定义类名 */
  className?: string
  /** 扫描方向 */
  direction?: 'vertical' | 'horizontal'
}

/**
 * 扫描覆盖层组件
 * 提供可自定义的激光扫描特效动画，支持垂直和水平扫描
 *
 * @example
 * ```tsx
 * <ScanningOverlay
 *   visible={true}
 *   width={300}
 *   height={400}
 *   laserColor="#0093FF"
 *   duration={2}
 *   direction="vertical"
 * />
 * ```
 */
const ScanningOverlay: React.FC<ScanningOverlayProps> = React.memo(
  ({
    visible = true,
    width = 288,
    height = 384,
    laserColor = '#0093FF',
    duration = 2,
    className = '',
    direction = 'vertical'
  }) => {
    if (!visible) {
      return null
    }

    const containerClasses = ['scanning-overlay-container', className]
      .filter(Boolean)
      .join(' ')

    const scanLineClasses = [
      'scanning-overlay-line',
      `scanning-overlay-line--${direction}`
    ].join(' ')

    return (
      <div
        className={containerClasses}
        style={{
          width: `${width}px`,
          height: `${height}px`
        }}
      >
        {/* 激光扫描线 */}
        <div
          className={scanLineClasses}
          style={{
            backgroundColor: laserColor,
            boxShadow: `0 0 10px ${laserColor}, 0 0 20px ${laserColor}, 0 0 30px ${laserColor}`,
            animationDuration: `${duration}s`
          }}
        />

        {/* 扫描网格背景 */}
        <div className="scanning-overlay-grid" />

        {/* 扫描边框 */}
        <div
          className="scanning-overlay-border"
          style={{
            borderColor: laserColor,
            boxShadow: `inset 0 0 20px ${laserColor}40`
          }}
        />
      </div>
    )
  }
)

ScanningOverlay.displayName = 'ScanningOverlay'

export default ScanningOverlay
